<template>
    <component :is="activeLayout">
        <div v-show="!isWelcome" class="w-full xl:absolute xl:top-5 xl:left-0">
            <div class="max-w-md mx-auto"><img v-if="form.series_type == 'nsight'" :src="asset('img/nsight.png')" class="max-h-[45px] mb-10 ml-auto" alt="nsight"></div>
        </div>
        <app-errors :form="form" class="mb-6"></app-errors>

        <div v-if="!showReview && !isWelcome">
            <div v-show="!validated">
                <h2 class="mb-4 text-2xl font-bold text-black">Create Your User Profile</h2>

                <div class="space-y-4">
                    <text-field v-model="form.email" label="Email Address" :required="true"></text-field>

                    <text-field v-model="form.first_name" label="First Name" :required="true"></text-field>

                    <text-field v-model="form.last_name" label="Last Name" :required="true"></text-field>

                    <text-field v-model="form.password" type="password" label="Password" :required="true"></text-field>

                    <text-field v-model="form.password_confirmation" type="password" label="Confirm Password" :required="true"></text-field>

                    <checkbox-field v-model="form.terms">
                        <span class="text-base font-medium text-gray-400">By clicking "Next" I agree and accept the <a href="https://mammoth.vc/terms/" class="font-bold text-black" target="_blank">Terms of Service</a>.</span>
                    </checkbox-field>
                </div>

                <div class="flex justify-end space-x-6 mt-14">
                    <app-button @click="handleLogin" :busy="form.busy">Next</app-button>
                </div>

                <div class="mt-14">
                    <p class="text-lg text-gray-400">Already have an account? <Link :href="route('login')" class="font-bold text-black">Login here</Link></p>
                </div>
            </div>

            <div v-show="validated">
                <h2 class="mb-4 text-2xl font-bold text-black">Verify Email Address</h2>

                <p class="mb-6 text-lg">A message has been sent to you to verify your email address. Please check your email and enter the verification code that you received.</p>

                <codes-field v-model="verificationCode" label="Verification Code" :required="true"></codes-field>

                <div class="flex justify-end space-x-6 mt-14">
                    <app-button @click="handleBack" class="!border-gray-200 !bg-white !text-gray-400">Back</app-button>

                    <app-button @click="handleVerification" :busy="form.busy">Next</app-button>
                </div>
                <div class="mt-14">
                    <p class="text-lg text-gray-400">Didn’t received an email? <a href="#" @click.prevent="handleResend" class="font-bold text-black">Resend code</a></p>
                </div>
            </div>
        </div><!-- SIGNUP FORM -->

        <div v-if="showReview && !isWelcome" class="relative">
            <h2 class="mb-4 text-2xl font-bold text-black">Terms, Conditions & Policies</h2>
            <p class="pb-14">Before moving forward on the investment process, please carefully review the documents below.</p>

            <div class="flex items-center mb-6 space-x-10 sm:space-x-20">
                <span @click="scrollTab('terms')" class="block font-bold text-gray-400 cursor-pointer" :class="{'text-black': reviewTab == 'terms'}">Terms and Conditions</span>
                <span @click="scrollTab('disclosure')" class="block font-bold text-gray-400 cursor-pointer" :class="{'text-black': reviewTab == 'disclosure'}">EFT Disclosure</span>
                <span @click="scrollTab('privacy')" class="block font-bold text-gray-400 cursor-pointer" :class="{'text-black': reviewTab == 'privacy'}">Privacy Policy</span>
            </div>

            <div @scroll="handleScroll" ref="reviewAgreement" class="h-screen sm:h-[530px] overflow-y-auto mb-8 space-y-6" :class="{'mb-[200px]': isMobile}">
                <div ref="termsCopy">
                    <terms-copy></terms-copy>
                </div>

                <div ref="disclosureCopy">
                    <disclosure-copy></disclosure-copy>
                </div>

                <div ref="privacyCopy">
                    <privacy-copy></privacy-copy>
                </div>
            </div>

            <div :class="{'fixed bottom-0 left-0 w-full bg-white py-6 terms-action': isMobile}">
                <div :class="{'container': isMobile}">
                    <checkbox-field v-model="form.reviewed">I have read the terms, conditions and policies.</checkbox-field>

                    <div class="flex justify-end space-x-6 mt-14" :class="{'!mt-3': isMobile}">
                        <app-button @click="handleReviewBack" class="!border-gray-200 !bg-white !text-gray-400">Back</app-button>

                        <app-button @click="handleReviewNext" :busy="form.busy" :disabled="!form.reviewed">Next</app-button>
                    </div>
                </div>
            </div>

            <div class="w-full h-20"></div>
        </div><!-- SIGNUP REVIEW -->

        <div v-if="isWelcome">
            <h1 class="mb-4 text-6xl font-bold text-center">Welcome to Mammoth</h1>
            <h4 class="mb-10 text-3xl text-center">Invest Beyond the Ordinary™️</h4>

            <div class="pb-20 text-center"><app-button @click="handleProceed" class="!py-4 !px-8 font-bold" :busy="isProcessing">Proceed</app-button></div>
        </div>

        <app-modal :show="isResend" :closeable="true" @close="isResend = false" class="sm:max-w-md">
            <div class="px-5 py-6 text-center bg-white rounded-md shadow-sm">
                <svg class="w-12 h-12 mx-auto mb-2 text-green-400" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path></svg>
                <p class="text-xl font-medium">Verification code has been sent</p>
            </div>
        </app-modal>
    </component>
</template>

<script>
import AuthLayout from '@/Layouts/Auth'
import AuthTosLayout from '@/Layouts/AuthTos'
import AuthFullLayout from '@/Layouts/AuthFull'
import AppModal from '@Components/Modal'
import AppButton from '@Components/Button'
import AppErrors from '@Components/Errors'
import TextField from '@Fields/Text'
import CheckboxField from '@Fields/Checkbox'
import CodesField from '@Fields/Codes'
import TermsCopy from '@/Pages/Auth/Terms'
import PrivacyCopy from '@/Pages/Auth/Privacy'
import DisclosureCopy from '@/Pages/Auth/EFTDisclosure.vue'
import { computed, onMounted, ref } from 'vue'
import useForm from '@/form'
import { useWindowSize } from '@vueuse/core'
import { Link } from '@inertiajs/inertia-vue3'

export default {
    components: {
        AuthLayout,
        AuthTosLayout,
        AuthFullLayout,
        AppModal,
        AppButton,
        AppErrors,
        TextField,
        CheckboxField,
        CodesField,
        TermsCopy,
        PrivacyCopy,
        DisclosureCopy,
        Link
    },
    props: {
        userData: {
            type: Object,
            default: () => ({})
        }
    },
    setup(props){
        const validated = ref(false)
        const verificationCode = ref('')
        const generatedCode = ref('')
        const isResend = ref(false)
        const isWelcome = ref(false)
        const isProcessing = ref(false)
        const showReview = ref(false)
        const reviewTab = ref('terms')

        const reviewAgreement = ref(null)
        const termsCopy = ref(null)
        const disclosureCopy = ref(null)
        const privacyCopy = ref(null)

        const form = useForm(_.assign({
            email: '',
            first_name: '',
            last_name: '',
            password: '',
            password_confirmation: '',
            series_type: _.get(route().params, 'series', 'default'),
            terms: false,
            verified: false,
            reviewed: false
        }, props.userData))

        const activeLayout = computed(() => {
            return (isWelcome.value) ? 'auth-full-layout' : (showReview.value ? 'auth-tos-layout' : 'auth-layout')
        })

        const isMobile = computed(() => {
            const { width } = useWindowSize()

            return width.value < 576
        })

        onMounted(() => {
            if (_.has(props.userData, 'code') && props.userData.code != '') {
                validated.value = true
                generatedCode.value = props.userData.code
                form.password_confirmation = form.password
            }
        })

        function scrollTab(tab){
            if (tab == reviewTab.value) {
                return false
            }

            reviewTab.value = tab

            var tabDivPosition = 0

            switch(tab){
                case 'disclosure':
                    tabDivPosition = disclosureCopy.value.offsetTop - 240
                    break;
                case 'privacy':
                    tabDivPosition = privacyCopy.value.offsetTop - 240
                    break;
            }

            reviewAgreement.value.scrollTop = tabDivPosition
        }

        function isElementVisible (el, holder) {
            const { top, bottom, height } = el.getBoundingClientRect()
            const holderRect = holder.getBoundingClientRect()

            return top <= holderRect.top ? holderRect.top - top <= height : bottom - holderRect.bottom <= height
        }

        function handleScroll(evt){
            if (isElementVisible(termsCopy.value, reviewAgreement.value)) {
                reviewTab.value = 'terms'
            }

            if (isElementVisible(disclosureCopy.value, reviewAgreement.value)) {
                reviewTab.value = 'disclosure'
            }

            if (isElementVisible(privacyCopy.value, reviewAgreement.value)) {
                reviewTab.value = 'privacy'
            }
        }

        function handleBack(){
            validated.value = false
            generatedCode.value = ''
            verificationCode.value = ''
            form.resetData()
        }

        function handleNext(e){
            form.post(route('signup')).then(response => {
                validated.value = true
                generatedCode.value = response.data
            }).catch(errors => {
                window.scroll(0, 0)
            });
        }

        function handleVerification(){
            form.reset()

            if (generatedCode.value != verificationCode.value) {
                form.errors.set({
                    error: 'Invalid verification code'
                })

                return false;
            }

            form.verified = true
            isWelcome.value = true
        }

        function handleResend(){
            isResend.value = true
            handleNext(null);
        }

        function handleReview(){
            isProcessing.value = true

            form.post(route('signup')).then(response => {
                window.location.href = route('index')
            });
        }

        function handleProceed(){
            isWelcome.value = false
            showReview.value = true
        }

        function handleReviewBack(){
            handleBack()
        }

        function handleReviewNext(){
            if (form.reviewed) {
                handleReview()
            }
        }

        function handleLogin(){
            window.location.href = route('welcome');
        }

        return {
            isResend,
            isWelcome,
            isProcessing,
            form,
            showReview,
            reviewTab,
            reviewAgreement,
            termsCopy,
            disclosureCopy,
            privacyCopy,
            activeLayout,
            isMobile,
            verificationCode,
            validated,
            scrollTab,
            handleBack,
            handleNext,
            handleVerification,
            handleResend,
            handleReview,
            handleProceed,
            handleReviewBack,
            handleReviewNext,
            handleScroll,
            handleLogin
        }
    }
}
</script>

<style scoped>
.terms-action {
    box-shadow: 0 -25px 50px -12px rgba(0, 0, 0, 0.1);
}
</style>
